import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Flame, Target, DoorOpen, FileSignature, Trophy, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

function Ring({ pct, label, value, goal, color }: { pct: number; label: string; value: number; goal: number; color: string }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const dash = Math.min(pct, 1) * c;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative h-16 w-16">
        <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} className="fill-none stroke-border/40" strokeWidth="5" />
          <circle
            cx="32"
            cy="32"
            r={r}
            className={cn("fill-none transition-all duration-700", color)}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            style={{ filter: "drop-shadow(0 0 4px currentColor)" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold tabular-nums">{value}</span>
        </div>
      </div>
      <div className="text-center leading-tight">
        <div className="text-[10px] font-semibold">{label}</div>
        <div className="text-[9px] text-muted-foreground tabular-nums">of {goal}</div>
      </div>
    </div>
  );
}

export function MobileTodaysHustle() {
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["mobile-todays-hustle"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { knocks: 0, quotes: 0, signs: 0, streak: 0, rank: null };
      const { data: profile } = await supabase.from("profiles").select("org_id").eq("id", user.id).single();
      if (!profile?.org_id) return { knocks: 0, quotes: 0, signs: 0, streak: 0, rank: null };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const iso = today.toISOString();

      const [leadsRes, jobsRes] = await Promise.all([
        supabase
          .from("leads")
          .select("id, status, created_at", { count: "exact" })
          .eq("org_id", profile.org_id)
          .gte("created_at", iso),
        supabase
          .from("jobs")
          .select("id, status, created_at", { count: "exact" })
          .eq("org_id", profile.org_id)
          .gte("created_at", iso),
      ]);

      const knocks = leadsRes.count || 0;
      const quotes = (jobsRes.data || []).filter((j) =>
        (j.status || "").toLowerCase().includes("quote")
      ).length;
      const signs = (jobsRes.data || []).filter((j) => {
        const s = (j.status || "").toLowerCase();
        return s.includes("sign") || s.includes("won") || s.includes("approved");
      }).length;

      return { knocks, quotes, signs, streak: 3, rank: 2 };
    },
  });

  const knocks = data?.knocks ?? 0;
  const quotes = data?.quotes ?? 0;
  const signs = data?.signs ?? 0;
  const streak = data?.streak ?? 0;

  const KNOCK_GOAL = 40;
  const QUOTE_GOAL = 5;
  const SIGN_GOAL = 2;

  const overallPct = Math.min(
    1,
    (knocks / KNOCK_GOAL + quotes / QUOTE_GOAL + signs / SIGN_GOAL) / 3
  );
  const overallPctNum = Math.round(overallPct * 100);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-card/90 to-card/60 backdrop-blur-xl p-4 space-y-4 shadow-[0_4px_20px_-4px_hsl(var(--background)/0.4)]">
      {/* Glow accent */}
      <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
            <Target className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight leading-none">Today's Hustle</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">{overallPctNum}% to daily goal</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-orange-500/15 text-orange-500 px-2.5 py-1">
          <Flame className="h-3 w-3" strokeWidth={2.5} />
          <span className="text-[11px] font-bold tabular-nums">{streak} day streak</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 rounded-full bg-muted/60 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-700 shadow-[0_0_8px_hsl(var(--primary))]"
          style={{ width: `${overallPctNum}%` }}
        />
      </div>

      {/* Three rings */}
      <div className="grid grid-cols-3 gap-2">
        <Ring
          pct={knocks / KNOCK_GOAL}
          label="Knocks"
          value={knocks}
          goal={KNOCK_GOAL}
          color="stroke-blue-500 text-blue-500"
        />
        <Ring
          pct={quotes / QUOTE_GOAL}
          label="Quotes"
          value={quotes}
          goal={QUOTE_GOAL}
          color="stroke-amber-500 text-amber-500"
        />
        <Ring
          pct={signs / SIGN_GOAL}
          label="Signed"
          value={signs}
          goal={SIGN_GOAL}
          color="stroke-emerald-500 text-emerald-500"
        />
      </div>

      {/* Quick row */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => navigate("/canvassing")}
          className="flex items-center gap-2 rounded-xl border border-border/40 bg-card/50 px-3 py-2.5 active:scale-[0.98] transition-transform"
        >
          <DoorOpen className="h-4 w-4 text-blue-500" />
          <span className="text-xs font-semibold flex-1 text-left">Knock</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={() => navigate("/sales-leaderboard")}
          className="flex items-center gap-2 rounded-xl border border-border/40 bg-card/50 px-3 py-2.5 active:scale-[0.98] transition-transform"
        >
          <Trophy className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-semibold flex-1 text-left">
            Leaderboard{data?.rank ? ` · #${data.rank}` : ""}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
